/**
 * @fileoverview O cadastro financeiro da Empresa (31/10/2026, etapa 6 dos
 * planos), em Configurações → Faturamento: pessoa física (CPF) ou jurídica
 * (CNPJ), o nome ou a razão social, o e-mail de cobrança, o telefone e o
 * endereço completo, que o cartão exige. O servidor confere cada campo
 * (o código do erro vira o texto) e, com a cobrança ligada, deixa o cliente
 * pronto na Pagar.me. A seção só aparece para quem gere a cobrança
 * (billing.manage), e a API também recusa os outros. Os campos e o estado são
 * os mesmos da contratação (03/11/2026).
 */

import React from "react";
import { toast } from "react-toastify";
import { CircularProgress, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import UnsavedChangesHint from "../UnsavedChangesHint";
import { tokens } from "../Triage/styles";
import toastError from "../../errors/toastError";
import { useReportDirty } from "../../hooks/useLeaveGuard";
import { i18n } from "../../translate/i18n";
import BillingProfileFields from "./BillingProfileFields";
import { useBillingProfile } from "./useBillingProfile";

const t = (key) => i18n.t(`billingProfile.${key}`);

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  const xs = theme.breakpoints.down("xs");
  return {
    card: { padding: theme.spacing(2.5), borderRadius: 12, background: c.surface, border: `1px solid ${c.border}`, marginBottom: theme.spacing(2.5) },
    title: { margin: 0, fontSize: "1rem", fontWeight: 700, color: dark ? "#f8fafc" : "#111827" },
    subtitle: { margin: "4px 0 18px", fontSize: "0.82rem", lineHeight: 1.5, color: theme.palette.text.secondary },
    grid: { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: theme.spacing(1.75), [xs]: { gridTemplateColumns: "1fr" } },
    span2: { gridColumn: "span 2", [xs]: { gridColumn: "auto" } },
    span3: { gridColumn: "span 3", [xs]: { gridColumn: "auto" } },
    span4: { gridColumn: "span 4", [xs]: { gridColumn: "auto" } },
    field: { display: "flex", flexDirection: "column", gap: 5, minWidth: 0 },
    label: { fontSize: "0.8rem", fontWeight: 600, color: dark ? "#f8fafc" : "#111827" },
    optional: { fontWeight: 400, color: theme.palette.text.secondary },
    zipBusy: { marginLeft: 6, verticalAlign: "-1px" },
    input: {
      height: 42,
      borderRadius: 8,
      border: `1px solid ${c.inputBorder}`,
      background: dark ? "rgba(255,255,255,0.05)" : "#fff",
      color: dark ? "#f8fafc" : "#111827",
      padding: "0 12px",
      fontSize: "0.9rem",
      fontFamily: "inherit",
      width: "100%",
      boxSizing: "border-box",
      outline: "none",
      transition: "border-color 120ms",
      "&:focus": { borderColor: "#3D5CFD" },
      "&::placeholder": { color: dark ? "rgba(248,250,252,0.35)" : "#9ca3af" },
    },
    inputError: { borderColor: c.bad, "&:focus": { borderColor: c.bad } },
    fieldError: { fontSize: "0.76rem", lineHeight: 1.4, color: c.bad },
    // "&&": as regras do select padrão empatariam. Mesma altura e raio dos campos.
    select: { "&& .MuiOutlinedInput-root": { height: 42, borderRadius: 8 } },
    selectError: { "&& .MuiOutlinedInput-notchedOutline": { borderColor: `${c.bad} !important` } },
    actions: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: theme.spacing(1.5), marginTop: theme.spacing(2.5) },
    primaryBtn: {
      borderRadius: 8,
      border: "none",
      height: 40,
      padding: "0 22px",
      cursor: "pointer",
      fontSize: "0.88rem",
      fontWeight: 700,
      fontFamily: "inherit",
      color: "#fff",
      background: "#3D5CFD",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      "&:disabled": { opacity: 0.6, cursor: "default" },
    },
  };
});

const BillingProfileForm = ({ onDirtyChange, onSaved }) => {
  const classes = useStyles();
  const billing = useBillingProfile();
  useReportDirty(billing.dirty, onDirtyChange);

  const save = async () => {
    const result = await billing.save();
    if (!result.ok) {
      if (!result.handled) toastError(result.err);
      return;
    }
    toast.success(t("saved"));
    if (onSaved) onSaved();
  };

  if (billing.loading) return null;

  return (
    <Paper elevation={0} className={classes.card}>
      <h2 className={classes.title}>{t("title")}</h2>
      <p className={classes.subtitle}>{t("subtitle")}</p>
      <BillingProfileFields classes={classes} billing={billing} />
      <div className={classes.actions}>
        <UnsavedChangesHint show={billing.dirty} />
        <button type="button" className={classes.primaryBtn} onClick={save} disabled={billing.saving || billing.zipLoading || !billing.dirty}>
          {billing.saving && <CircularProgress size={15} color="inherit" />}
          {t("save")}
        </button>
      </div>
    </Paper>
  );
};

export default BillingProfileForm;
